import React from "react";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { Grid, Loader } from "semantic-ui-react";
import "moment/locale/pt-br";
import { toast } from "react-toastify";
import errors from "../../../Utils/ExtractErrors";
import CalendarioRepository from "../../../Repositories/CalendarioRepository";
import { ICalendario } from "../../../interfaces";
import CadastrarEvento from "./CadastrarEvento";
import ModalYesNo from "../../../components/ModalYesNo";
import CustomLoader from "../../../components/CustomLoader";

const localizer = momentLocalizer(moment);

const defaultMessages = {
    date: "Data",
    time: "Hora",
    event: "Evento",
    allDay: "Dia Todo",
    week: "Semana",
    work_week: "Eventos",
    day: "Dia",
    month: "Mês",
    previous: "Anterior",
    next: "Próximo",
    yesterday: "Ontem",
    tomorrow: "Amanhã",
    today: "Hoje",
    agenda: "Agenda",
    noEventsInRange: "Não há eventos no período.",
    showMore: function showMore(total: number) {
        return "+" + total + " mais";
    }
};

interface IScheduleProps {
    isAdmin: boolean;
}

interface IScheduleState {
    loading: boolean;
    open: boolean;
    eventId: string;
    eventTitle: string;
    eventDay: number;
    schedules: ICalendario[];
}

interface IEvent {
    id: string;
    day: number;
    title: string;
    start: Date;
    end: Date;
}

const initialState: IScheduleState = {
    loading: false,
    open: false,
    eventId: "",
    eventTitle: "",
    eventDay: 0,
    schedules: []
}

export default function Schedule({ isAdmin }: IScheduleProps) {
    const [state, setState] = React.useState<IScheduleState>(initialState);

    React.useEffect(() => {
        load()
    }, []);

    async function load() {
        try {
            setState(state => ({ ...state, loading: true }));
            const response = await CalendarioRepository.GetSchedule();
            setState(state => ({ ...state, schedules: response, loading: false }));
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
    }

    function getEvents() {
        const events: IEvent[] = [];

        state.schedules.forEach(schedule => {
            schedule.eventos.forEach(evento => events.push({
                id: evento.id,
                day: schedule.diaDoMes,
                title: evento.titulo,
                start: new Date(evento.dataInicio),
                end: new Date(evento.dataFim)
            }))
        });

        return events;
    }

    function handleOnDelete(event: IEvent, e: React.SyntheticEvent<HTMLElement, Event>) {
        setState(state => ({
            ...state,
            eventDay: event.day,
            eventId: event.id,
            eventTitle: event.title,
            open: true
        }));
    }

    function handleOnDeleteCancel() {
        setState(state => ({
            ...state,
            eventDay: 0,
            eventId: "",
            eventTitle: "",
            open: false
        }));
    }

    async function handleOndeleteConfirm() {
        try {
            setState(state => ({ ...state, loading: true }));
            const response = await CalendarioRepository.DeleteEvent({
                diaDoMes: state.eventDay,
                eventoId: state.eventId
            });
            toast(`🎉 ${response.message}`, { position: "top-center", type: "success" });
            setState(state => ({ ...state, open: false }));
            load();
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getApiErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
    }

    return (
        <>
            {state.loading
                ? <CustomLoader />
                : <>
                    {isAdmin &&
                        <>
                            <Grid.Row>
                                <Grid.Column width="16" textAlign="right">
                                    <CadastrarEvento onSuccess={load} />
                                </Grid.Column>
                            </Grid.Row>
                            <RemoverEvento
                                open={state.open}
                                title={state.eventTitle}
                                onSuccess={handleOndeleteConfirm}
                                onCancel={handleOnDeleteCancel}
                            />
                        </>
                    }
                    <Grid.Row>
                        <Grid.Column width="16">
                            <Calendar
                                localizer={localizer}
                                events={getEvents()}
                                startAccessor="start"
                                endAccessor="end"
                                titleAccessor="title"
                                messages={defaultMessages}
                                style={{ height: "70vh" }}
                                onDoubleClickEvent={handleOnDelete}
                            />
                        </Grid.Column>
                    </Grid.Row>
                </>}
        </>
    );
}

interface IRemoverEventoProps {
    open: boolean;
    title: string;
    onSuccess: () => void;
    onCancel: () => void;
}

function RemoverEvento({ open, title, onSuccess, onCancel }: IRemoverEventoProps) {
    return (
        <ModalYesNo
            open={open}
            header="Apagar"
            content={`Deseja realmente apagar o evento ${title}?`}
            onSuccess={onSuccess}
            onCancel={onCancel}
        />
    );
}